'use client';
import React, { forwardRef } from 'react';

import { cn } from '@fshr/shared-react';
import isEmpty from 'lodash/isEmpty';

interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
	options: Array<{
		label: string;
		value: string | number;
		className?: string;
		disabled?: boolean;
	}>;
	wrapperClassName?: string;
	selectClassName?: string;
	placeholder?: string;
	label?: string;
	required?: boolean;
	readonly?: boolean;
	errorMessage?: React.ReactNode;
	fullWidth?: boolean;
	labelClassName?: string;
	labelTextClassName?: string;
}

const Select = forwardRef(
	(
		{
			id,
			name,
			options,
			wrapperClassName,
			selectClassName,
			className,
			label,
			value,
			defaultValue,
			placeholder,
			required,
			readonly,
			errorMessage,
			fullWidth = false,
			labelClassName,
			labelTextClassName,
			...props
		}: SelectProps,
		ref: React.Ref<HTMLSelectElement>
	) => {
		return (
			<div
				className={cn('relative pb-[20px] lg:pb-[28px]', wrapperClassName, {
					'pt-200': label,
					'text-red-700': errorMessage,
					'w-full': fullWidth,
				})}
			>
				<div
					className={
						!readonly
							? cn(
									[
										'flex',
										'flex-row',
										'relative',
										'mt-[2px]',

										// arrow
										'after:pointer-events-none',
										"after:content=['']",
										'after:border-[6px]',
										'after:border-l-transparent',
										'after:border-r-transparent',
										'after:border-b-0',
										'after:border-t-black',
										'after:absolute',
										'after:top-1/2',
										'after:-translate-y-1/2',
										'ltr:after:right-[16px]',
										'rtl:after:left-0',
										'border-b',
										'border-black',
									],
									{
										'border-red-700 after:text-red-700 after:border-t-red-700': errorMessage,
									},
									className
								)
							: 'relative readonly-span text-body2'
					}
				>
					<select
						id={id}
						name={name || id}
						className={cn(
							[
								'peer',
								'appearance-none',
								'bg-transparent',
								'transition-colors',
								'text-ellipsis',
								'whitespace-nowrap',
								'[text-transform:inherit]',
								'ltr:pr-200',
								'rtl:pl-200',
								'overflow-hidden',
								'w-full',
								'h-full',
								'duration-300',
							],
							{
								'px-4 cursor-pointer': !readonly,
								'text-black opacity-100 cursor-not-allowed': readonly,
								'text-red-700': errorMessage,
								'focus:text-transparent': placeholder && isEmpty(value),
							},
							selectClassName
						)}
						{...props}
						ref={ref}
						disabled={readonly}
						value={value || (placeholder && '')}
						required={required}
						aria-labelledby={label && `${id}-label`}
						aria-invalid={errorMessage ? true : undefined}
						aria-describedby={
							errorMessage && id ? `${id}-error` : (props['aria-describedby'] as string | undefined)
						}
					>
						{placeholder && (
							<option value={''} disabled>
								{`${placeholder}${required ? '*' : ''}`}
							</option>
						)}
						{options.map(({ label, value, className, disabled: optionDisabled }) => (
							<option
								className={cn(
									'text-text-primary',
									{
										'text-red-700': errorMessage,
										'text-text-disabled': optionDisabled,
									},
									className
								)}
								key={`${value}-${label}`}
								value={value}
								disabled={optionDisabled}
							>
								{/* eslint-disable-next-line no-restricted-syntax -- <option> content is text-only and can't hold a <span> */}
								{label}
							</option>
						))}
					</select>
					{label && (
						<label
							className={cn(
								'text-label uppercase absolute duration-300 opacity-100 left-0 top-0 -translate-y-[18px] peer-focus:opacity-100 peer-focus:-translate-y-[18px]',
								{
									'opacity-0 translate-y-0': placeholder && (!value || value === ''),
								},
								labelClassName
							)}
							htmlFor={id}
							id={id && `${id}-label`}
						>
							<span className={labelTextClassName}>{`${label}${required ? '*' : ''}`}</span>
						</label>
					)}
				</div>
				{errorMessage && (
					<span
						id={id ? `${id}-error` : undefined}
						className="absolute lg:pt-[2px] text-caption text-red-700"
						role="alert"
					>
						{errorMessage}
					</span>
				)}
			</div>
		);
	}
);

export default Select;
